// ═══════════════════════════════════════════════════════════════════════════════
// Admin — audit log search
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import type { AdminPortalAuditLogListResponse } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { AdminIpWithGeo } from '../../components/admin/AdminIpWithGeo';

export const AdminAuditLogsPage = () => {
  const { t } = useLanguage();
  const [userEmail, setUserEmail] = useState('');
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminPortalAuditLogListResponse | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const q = new URLSearchParams({
        page: String(page),
        pageSize: '50',
      });
      if (userEmail.trim()) {
        q.set('userEmail', userEmail.trim());
      }
      if (action.trim()) {
        q.set('action', action.trim());
      }
      if (targetType.trim()) {
        q.set('targetType', targetType.trim());
      }
      if (startDate) {
        q.set('startDate', startDate);
      }
      if (endDate) {
        q.set('endDate', endDate);
      }
      const res = await adminApi.get<AdminPortalAuditLogListResponse>(
        `/admin/audit-logs?${q.toString()}`
      );
      if (res.success && res.data) {
        setData(res.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [action, endDate, page, startDate, targetType, t, userEmail]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.audit')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.auditSubtitle')}</p>

      <div className="mt-6 flex flex-wrap gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <input
          value={userEmail}
          onChange={(ev) => setUserEmail(ev.target.value)}
          placeholder="User Email"
          className="min-w-[200px] flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.auditUserId')}
        />
        <input
          value={action}
          onChange={(ev) => setAction(ev.target.value)}
          placeholder={t('admin.auditAction')}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.auditAction')}
        />
        <input
          value={targetType}
          onChange={(ev) => setTargetType(ev.target.value)}
          placeholder={t('admin.auditTarget')}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.auditTarget')}
        />
        <IsoDatePicker
          id="admin-audit-start"
          ariaLabel={t('admin.auditStart')}
          value={startDate}
          onChange={setStartDate}
          maxDate={endDate || undefined}
          appearance="admin"
          className="min-w-[10rem]"
        />
        <IsoDatePicker
          id="admin-audit-end"
          ariaLabel={t('admin.auditEnd')}
          value={endDate}
          onChange={setEndDate}
          minDate={startDate || undefined}
          appearance="admin"
          className="min-w-[10rem]"
        />
        <button
          type="button"
          className="rounded-md bg-slate-800 px-4 py-2 text-sm"
          onClick={() => void load()}
        >
          {t('task.action.refresh')}
        </button>
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-800 text-xs">
        <table className="min-w-full divide-y divide-slate-800 text-left">
          <thead className="bg-slate-900/80 text-slate-400">
            <tr>
              <th className="px-2 py-2">{t('admin.auditColTime')}</th>
              <th className="px-2 py-2">{t('admin.auditColAction')}</th>
              <th className="px-2 py-2">{t('admin.auditColUser')}</th>
              <th className="px-2 py-2">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 text-slate-300">
            {(data?.logs ?? []).map((row) => (
              <tr key={row.id}>
                <td className="whitespace-nowrap px-2 py-2">{row.createdAt}</td>
                <td className="px-2 py-2">{row.action}</td>
                <td className="px-2 py-2">{row.appUserEmail ?? row.appUserId ?? '—'}</td>
                <td className="px-2 py-2">
                  <AdminIpWithGeo ip={row.ip} geo={row.ipGeo} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          className="rounded-md border border-slate-700 px-3 py-1 text-sm disabled:opacity-40"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('admin.prevPage')}
        </button>
        <button
          type="button"
          disabled={data ? page * data.pageSize >= data.totalCount : true}
          className="rounded-md border border-slate-700 px-3 py-1 text-sm disabled:opacity-40"
          onClick={() => setPage((p) => p + 1)}
        >
          {t('admin.nextPage')}
        </button>
      </div>
    </div>
  );
};
